import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { Identity } from '../viewmodels/login/identity';
import { CONFIG, Configuration } from './config';
import { PermissionsService } from './permissions.service';
import { RemoteConfiguration } from './remote-config';
import { WINDOW } from './window-provider';

@Injectable()
export class ConfigService {
  /**
   * The configuration info returned from the API.
   */
  private remoteConfig: RemoteConfiguration;

  /**
   * The configuration info retreived from a local config.
   */
  private config: Configuration;

  private configurationLoaded: Subject<Configuration> = new Subject<Configuration>();

  public selectedCatalog: BehaviorSubject<string> = new BehaviorSubject('');

  public IsError: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(@Inject(WINDOW) private window: Window, private http: HttpClient, private route: Router) {
    this.remoteConfig = new RemoteConfiguration();
  }


  /**
   * Loads information from a config file served locally.
   */
  public load(url: string, permissionService: PermissionsService) {
    return new Promise<void>((resolve, reject) => {
      this.http.get(window.location.origin + CONFIG.routes.authLoginValidator).subscribe(
        (response: any) => {
          if (response && 'userName' in response) {
            const username: string = response['userName'];

            localStorage.setItem('currentUser', username);
            const httpParam = new HttpParams().set('userName', username);

            this.http.get(window.location.origin + '/api/' + CONFIG.apiRoutes.userCatalog, { params: httpParam })
              .pipe(
                tap((result: unknown) => this.SetDefaultCatalog(result)),
                switchMap(() => this.http.get(url))
              )
              .subscribe(
                (config: Configuration) => {
                  this.config = config;
                  // Load remote configuration after local config since it depends on API base url
                  void this.loadRemote(permissionService).then(() => {
                    this.configurationLoaded.next(this.config);
                    resolve();
                  });
                },
                error => {
                  reject(error);
                }
              );
          }
        },
        error => {
          if (error) {
            const currentPath = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;

            window.location.href = `${window.location.origin + CONFIG.routes.authLogin}?landingpage=${currentPath}`;
          }
        }
      );
    });
  }

  /**
   * Makes a call to the API to retreive per-instance config settings.
   */
  public loadRemote(permissionService: PermissionsService): Promise<unknown> {
    const remoteConfigUrl = this.config.apiBaseUrl + '/' + CONFIG.apiRoutes.applicationConfig;
    const appUrl = this.window.location.origin;
    return new Promise(resolve => {
      this.http.get(remoteConfigUrl).subscribe((remoteConfig: RemoteConfiguration) => {
        this.remoteConfig.loadFromJson(remoteConfig, appUrl);
        void this.loadPermissions(this.config.apiBaseUrl, permissionService).then(() => {
          resolve(null);
        });
      });
    });
  }

  /**
   * Makes a call to the API to retreive permission settings.
   */
  public loadPermissions(url: string, permissionService: PermissionsService): Promise<unknown> {
    return new Promise<void>(resolve => {
      if (localStorage.getItem('usercatalogs')) {
        const lstItems = JSON.parse(localStorage.getItem('usercatalogs'));
        if (lstItems && lstItems.length == 0) {
          this.IsError.next(true);
          this.route.navigate([CONFIG.routes.catalogerror]);
          resolve(null);
          return;
        }
        const path = this.window.location.hash.split('/')[1];
        if (path === undefined || path === "") {
          this.setCatalog(lstItems[0].catalogName);
        } else {
          const pathhasValidCatalog = (lstItems.find(x => x.catalogName === path) !== undefined);
          if (pathhasValidCatalog) {
            this.setCatalog(path);
          } else {
            this.IsError.next(true);
            this.route.navigate([CONFIG.routes.catalogerror]);
            resolve(null);
          }
        }
      }


      const options = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }),
      };

      this.http.get(url + '/identity', options).subscribe(
        (identity: Identity) => {
          if (identity) {
            /* Code to handle the display of reset password link in left menu */
            localStorage.setItem('usingLoginProvider', identity.usingLoginProvider.toString());
            if (identity.passwordResetRequired && localStorage.getItem('already-reset') !== 'true') {
              localStorage.setItem('resetRequired', identity.passwordResetRequired.toString());
            }
            permissionService.SetPermissions(identity);
            resolve(null);
          }
        },
        () => resolve(null),
      );
    });
  }

  public getConfiguration(): Configuration {
    return this.config;
  }

  public async getConfigurationAsync(): Promise<Configuration> {
    if (this.config != null) {
      return this.config;
    }

    return new Promise<Configuration>(resolve => {
      this.configurationLoaded.pipe(take(1)).subscribe(x => {
        resolve(x);
      });
    });
  }

  public getRemoteConfiguration(): RemoteConfiguration {
    return this.remoteConfig;
  }

  public setCatalog(value: string) {
    this.selectedCatalog.next(value);
  }

  private SetDefaultCatalog(userCatalog): void {
    localStorage.setItem('usercatalogs', JSON.stringify(userCatalog));
  }

  get AnyError(): boolean {
    return this.IsError.getValue();
  }

}
